import type { CallStatusResourceResponseApi } from '@ingenico/api-sdk-device-management-private';
import { enumDeviceDiagnosticsType } from '@ingenico/api-sdk-device-management-private-v2';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUserDateTimeFormat, useCurrentUserTimezone } from '@ingenico/react-section-utils';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { ListViewItem } from '@ingenico/react-ui/datagrid';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DoDisturbAltOutlinedIcon from '@mui/icons-material/DoDisturbAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import type { SvgIconProps } from '@mui/material';
import { useTheme } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { toString } from 'lodash-es';
import type { ComponentType, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseDeviceDiagnosticsListContentColumns } from '../components/device-diagnostics-list-content/DeviceDiagnosticsListContent.model';
import type {
  DiagnosticTestStatus,
  MappedDiagnosticsResourceApi,
} from '../domain/models/DeviceDiagnosticsDetails.model';
import { enumDiagnosticTestStatus } from '../domain/models/DeviceDiagnosticsDetails.model';

export function useLogicalDeviceDiagnosticsListColumns(): UseDeviceDiagnosticsListContentColumns {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON, ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const theme = useTheme();
  const currentUserDateTimeFormat = useCurrentUserDateTimeFormat();
  const timezone = useCurrentUserTimezone();
  const { getUserDateAndTimeFormatWithTimezone } = useUserDateTimeFormat(currentUserDateTimeFormat);

  const statusIconMap: Record<DiagnosticTestStatus, ComponentType<SvgIconProps>> = useMemo(
    () => ({
      [enumDiagnosticTestStatus.PASSED]: CheckCircleOutlinedIcon,
      [enumDiagnosticTestStatus.WARNING]: WarningAmberOutlinedIcon,
      [enumDiagnosticTestStatus.FAILED]: CancelOutlinedIcon,
      [enumDiagnosticTestStatus.NOT_APPLICABLE]: DoDisturbAltOutlinedIcon,
    }),
    [],
  );

  const colorStatusIcon = useCallback((status: DiagnosticTestStatus): string => {
    switch (status) {
      case enumDiagnosticTestStatus.PASSED: {
        return theme.palette.success.main;
      }
      case enumDiagnosticTestStatus.FAILED: {
        return theme.palette.error.main;
      }
      case enumDiagnosticTestStatus.WARNING: {
        return theme.palette.warning.main;
      }
      case enumDiagnosticTestStatus.NOT_APPLICABLE: {
        return theme.palette.grey[500];
      }
      default: {
        return theme.palette.grey[300];
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderStatus = useCallback(
    (params: GridRenderCellParams, status: DiagnosticTestStatus): ReactNode => {
      const Icon = statusIconMap[status];
      const color = params.value === 0 ? theme.palette.grey[300] : colorStatusIcon(status);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color }}>
          <Icon fontSize="small" />
          <span>{params.value}</span>
        </div>
      );
    },
    [colorStatusIcon, statusIconMap, theme.palette.grey],
  );

  const renderCellValue = useCallback(
    (params: GridRenderCellParams): ReactNode => <ListViewItem primaryText={params.value as string} />,
    [],
  );

  const renderCellDateFormatter = useCallback(
    (params: GridRenderCellParams<CallStatusResourceResponseApi, Date>): ReactNode => {
      const [date, time] = getUserDateAndTimeFormatWithTimezone({
        instant: params?.value ?? '',
        timezone,
      });
      return (
        <div>
          <CoreTypography variant={CoreTypographyVariant.Body02Medium}>{date}</CoreTypography>
          <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{time}</CoreTypography>
        </div>
      );
    },
    [getUserDateAndTimeFormatWithTimezone, timezone],
  );

  const DEVICE_DIAGNOSTICS_COLUMNS = useMemo<GridColDef[]>(
    () => [
      {
        field: 'date',
        headerName: t('deviceDiagnostics.fields.date', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        filterable: true,
        flex: 1,
        renderCell: renderCellDateFormatter,
      },
      {
        field: 'type',
        headerName: t('deviceDiagnostics.fields.type', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderCellValue,
        valueGetter: (_, row: MappedDiagnosticsResourceApi) =>
          row.type === enumDeviceDiagnosticsType.AUTOMATIC_DIAGNOSTICS
            ? toString(t('deviceDiagnostics.status.automatic', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))
            : toString(t('deviceDiagnostics.status.manual', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })),
        filterable: false,
        sortable: false,
      },
      {
        field: 'serialNumber',
        headerName: t('deviceDiagnostics.fields.serialNumber', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderCellValue,
        filterable: false,
        sortable: false,
      },
      {
        field: 'testResults',
        headerName: t('deviceDiagnostics.fields.testResults', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderCellValue,
        valueGetter: (_, row: MappedDiagnosticsResourceApi) => row.testResults.length,
        filterable: false,
        sortable: false,
      },
      {
        field: 'success',
        headerName: t('deviceDiagnostics.fields.success', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: (params) => renderStatus({ ...params }, enumDiagnosticTestStatus.PASSED),
        valueGetter: (_, row: MappedDiagnosticsResourceApi) =>
          row.testResults.filter((result) => result.status === enumDiagnosticTestStatus.PASSED).length,
        filterable: false,
        sortable: false,
      },
      {
        field: 'warning',
        headerName: t('deviceDiagnostics.fields.warning', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: (params) => renderStatus({ ...params }, enumDiagnosticTestStatus.WARNING),
        valueGetter: (_, row: MappedDiagnosticsResourceApi) =>
          row.testResults.filter((result) => result.status === enumDiagnosticTestStatus.WARNING).length,
        filterable: false,
        sortable: false,
      },
      {
        field: 'failed',
        headerName: t('deviceDiagnostics.fields.failed', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: (params) => renderStatus({ ...params }, enumDiagnosticTestStatus.FAILED),
        valueGetter: (_, row: MappedDiagnosticsResourceApi) =>
          row.testResults.filter((result) => result.status === enumDiagnosticTestStatus.FAILED).length,
        filterable: false,
        sortable: false,
      },
      {
        field: 'notApplicable',
        headerName: t('deviceDiagnostics.fields.notApplicable', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: (params) => renderStatus({ ...params }, enumDiagnosticTestStatus.NOT_APPLICABLE),
        valueGetter: (_, row: MappedDiagnosticsResourceApi) =>
          row.testResults.filter((result) => result.status === enumDiagnosticTestStatus.NOT_APPLICABLE).length,
        filterable: false,
        sortable: false,
      },
    ],
    [renderCellDateFormatter, renderCellValue, renderStatus, t],
  );

  const defaultAvailableColumns = useMemo(
    () => DEVICE_DIAGNOSTICS_COLUMNS.map((col) => col.field),
    [DEVICE_DIAGNOSTICS_COLUMNS],
  );

  return {
    columns: DEVICE_DIAGNOSTICS_COLUMNS,
    defaultAvailableColumns,
  };
}
